import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

function subscribe(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

const read = (): boolean => navigator.onLine;

/** `navigator.onLine`, kept up to date. False is reliable; true can still mean a Wi-Fi without internet. */
export function useOnlineStatus(): boolean {
  return useSyncExternalStore(subscribe, read, () => true);
}

export type ServerReachability = 'checking' | 'reachable' | 'unreachable';

// A gym Wi-Fi without internet can leave the request hanging: give up early.
const PROBE_TIMEOUT_MS = 3_000;

export const probeServer = async (): Promise<boolean> => {
  try {
    const response = await fetch('/', {
      method: 'HEAD',
      cache: 'no-store',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Whether the app's own server answers, not just whether the device has a network.
 * Checked again when the browser comes back online and on `recheck`.
 */
export function useServerReachable(probe: () => Promise<boolean> = probeServer): {
  reachability: ServerReachability;
  recheck: () => void;
} {
  const online = useOnlineStatus();
  const [result, setResult] = useState<ServerReachability>('checking');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    setResult('checking');
    void probe().then((ok) => {
      if (!cancelled) setResult(ok ? 'reachable' : 'unreachable');
    });
    return () => {
      cancelled = true;
    };
  }, [online, attempt, probe]);

  const recheck = useCallback(() => setAttempt((n) => n + 1), []);

  return { reachability: online ? result : 'unreachable', recheck };
}
